import { z } from "zod";
//#region src/schema/plugin_source.ts
const RelativePluginSourceSchema = z.string().refine((value) => value.startsWith("./") && !value.split("/").includes(".."), { message: "relative source must start with \"./\" and must not contain \"..\" segments" });
const GithubPluginSourceSchema = z.object({
	ref: z.string().optional(),
	repo: z.string(),
	sha: z.string().optional(),
	source: z.literal("github")
});
const UrlPluginSourceSchema = z.object({
	ref: z.string().optional(),
	sha: z.string().optional(),
	source: z.literal("url"),
	url: z.string()
});
const GitSubdirPluginSourceSchema = z.object({
	path: z.string(),
	ref: z.string().optional(),
	sha: z.string().optional(),
	source: z.literal("git-subdir"),
	url: z.string()
});
const NpmPluginSourceSchema = z.object({
	package: z.string(),
	registry: z.string().optional(),
	source: z.literal("npm"),
	version: z.string().optional()
});
const PluginObjectSourceSchema = z.discriminatedUnion("source", [
	GithubPluginSourceSchema,
	UrlPluginSourceSchema,
	GitSubdirPluginSourceSchema,
	NpmPluginSourceSchema
]);
/** An object-form plugin source (github/url/git-subdir/npm). */
const PluginSourceSchema = z.union([RelativePluginSourceSchema, PluginObjectSourceSchema]);
/**
* Where to fetch a plugin listed in a marketplace catalog: a relative path
* (resolved against the marketplace root) or an object keyed by `source`.
*/
//#endregion
//#region src/schema/marketplace_source.ts
const UrlMarketplaceSourceSchema = z.object({
	ref: z.string().optional(),
	source: z.literal("url"),
	url: z.string()
});
const DirectoryMarketplaceSourceSchema = z.object({
	path: z.string(),
	source: z.literal("directory")
});
const MarketplaceSourceSchema = z.discriminatedUnion("source", [UrlMarketplaceSourceSchema, DirectoryMarketplaceSourceSchema]);
/** Where a marketplace catalog is fetched from: a git URL or a local directory. */
//#endregion
//#region src/schema/marketplace_name.ts
/** A marketplace name: kebab-case, used as the registry key and in `<plugin>@<marketplace>`. */
const MarketplaceNameSchema = z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "marketplace name must be kebab-case");
//#endregion
//#region src/schema/marketplace_plugin_entry.ts
const MarketplacePluginEntrySchema = z.object({
	defaultEnabled: z.boolean().optional(),
	description: z.string().optional(),
	name: MarketplaceNameSchema,
	source: PluginSourceSchema,
	version: z.string().optional()
}).loose();
/** A single plugin entry in a marketplace catalog. */
//#endregion
//#region src/schema/marketplace_catalog.ts
const MarketplaceMetadataSchema = z.object({
	description: z.string().optional(),
	/**
	* Optional prefix for plugin `source` paths in this catalog: each plugin's
	* `source` is resolved relative to it. Lets a catalog shorten repeated paths.
	*
	* @example
	* // these two catalogs describe the same plugin location:
	* { plugins: [{ name: 'fmt', source: './plugins/fmt' }] }
	* { metadata: { pluginRoot: './plugins' }, plugins: [{ name: 'fmt', source: 'fmt' }] }
	*/
	pluginRoot: z.string().optional(),
	version: z.string().optional()
}).loose();
const MarketplaceCatalogSchema = z.object({
	description: z.string().optional(),
	metadata: MarketplaceMetadataSchema.optional(),
	name: MarketplaceNameSchema,
	owner: z.object({
		email: z.string().optional(),
		name: z.string()
	}),
	plugins: z.array(MarketplacePluginEntrySchema),
	version: z.string().optional()
}).loose();
/** The `marketplace.json` catalog a marketplace publishes. */
//#endregion
//#region src/schema/plugin_manifest.ts
const PluginManifestSchema = z.object({
	defaultEnabled: z.boolean().optional(),
	description: z.string().optional(),
	name: z.string(),
	version: z.string().optional()
}).loose();
/** A plugin directories `plugin.json` manifest. */
//#endregion
//#region src/schema/known_marketplaces.ts
const KnownMarketplaceSchema = z.object({
	/** Whether plugins from this marketplace are updated automatically in the background. */
	autoUpdate: z.boolean().optional(),
	/** Absolute path of the cloned catalog under `<duoConfigDir>/marketplaces/<name>/`. */
	installLocation: z.string(),
	/** Commit SHA of the installed catalog; absent for directory sources. */
	installedRevision: z.string().optional(),
	lastUpdated: z.iso.datetime(),
	source: MarketplaceSourceSchema
});
/** A registered marketplace. */
const KnownMarketplacesFileSchema = z.record(z.string(), KnownMarketplaceSchema);
/** The `known_marketplaces.json` registry, keyed by marketplace name. */
//#endregion
//#region src/schema/installed_plugins.ts
const InstalledPluginSchema = z.object({
	installedAt: z.iso.datetime(),
	/** Shared cache directory `<duoConfigDir>/plugins/<marketplace>/<plugin>/<version>/`. */
	installPath: z.string(),
	lastUpdated: z.iso.datetime(),
	/**
	* The workspace a `project`/`local`-scoped install belongs to, so the same
	* plugin can be installed in several workspaces at once. Absent for `user`
	* scope, which is workspace-independent.
	*/
	projectPath: z.string().optional(),
	scope: z.enum([
		"user",
		"project",
		"local"
	]),
	version: z.string()
});
/** An installed plugin record, one per scope the plugin is installed in. */
const InstalledPluginsFileSchema = z.object({
	plugins: z.record(z.string(), z.array(InstalledPluginSchema)),
	version: z.literal(1)
});
/** The global `installed_plugins.json` ledger, keyed by `<plugin>@<marketplace>`. */
//#endregion
//#region src/schema/plugins.ts
const KnownMarketplaceEntrySchema = z.object({
	autoUpdate: z.boolean().optional(),
	source: MarketplaceSourceSchema
}).loose();
const PluginsConfigFileSchema = z.object({
	/** Plugin enablement, keyed by `<plugin>@<marketplace>`. */
	enabledPlugins: z.record(z.string(), z.boolean()).optional(),
	/** Marketplaces declared by this config, keyed by marketplace name. */
	extraKnownMarketplaces: z.record(MarketplaceNameSchema, KnownMarketplaceEntrySchema).optional()
}).loose();
/**
* The `plugins.json` config: which plugins are enabled and which marketplaces
* are known. Declared per scope (workspace `plugins.json` / `plugins.local.json`
* / user-level) and merged across scopes by precedence.
*/
//#endregion
export { KnownMarketplacesFileSchema as a, MarketplacePluginEntrySchema as c, PluginObjectSourceSchema as d, PluginSourceSchema as f, KnownMarketplaceSchema as i, MarketplaceNameSchema as l, InstalledPluginSchema as n, PluginManifestSchema as o, InstalledPluginsFileSchema as r, MarketplaceCatalogSchema as s, PluginsConfigFileSchema as t, MarketplaceSourceSchema as u };

//# sourceMappingURL=plugins-BXY_OlE0.mjs.map